import { captureHandledException } from '@/lib/sentry';
import React, { useState, useEffect } from 'react';
import { useLocation } from 'wouter';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Image from '@tiptap/extension-image';
import TextAlign from '@tiptap/extension-text-align';
import Underline from '@tiptap/extension-underline';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import { ArrowLeft, Save, Bold, Italic, Underline as UnderlineIcon, List, ListOrdered, Link as LinkIcon, Image as ImageIcon, AlignLeft, AlignCenter, AlignRight, FileText } from 'lucide-react';
import { useAuth } from '@/contexts/use-auth';
import { addVideo, updateVideo, getArticleById, getAuthors, AuthorData, uploadArticleContentImage, stringToTimestamp, timestampToString } from '@/lib/firebase';
import { ImageUpload } from '@/components/image-upload';
import { logger } from '@/lib/logger';

interface EditorToolbarProps {
  editor: any;
}

function EditorToolbar({ editor }: EditorToolbarProps) {
  if (!editor) return null;

  const addImage = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = async (event) => {
      const file = (event.target as HTMLInputElement).files?.[0];
      if (file) {
        try {
          // Validate file type
          if (!file.type.startsWith('image/')) {
            return;
          }
          
          // Validate file size (max 5MB)
          const maxSize = 5 * 1024 * 1024;
          if (file.size > maxSize) {
            return;
          }
          
          // Upload to Firebase Storage first
          const downloadURL = await uploadArticleContentImage(file);
          
          // Insert the image using TipTap's setImage command
          editor.chain().focus().setImage({ 
            src: downloadURL, 
            alt: 'Uploaded image'
          }).run();
          
        } catch (error) {
          logger.error('Error uploading image:', error);
        }
      }
    };
    input.click();
  };

  const addLink = () => {
    const url = window.prompt('Enter link URL:');
    if (url) {
      editor.chain().focus().setLink({ href: url }).run();
    }
  };

  return (
    <div className="border-b border-slate-200 p-3 flex flex-wrap gap-2">
      <Button
        variant={editor.isActive('bold') ? 'default' : 'ghost'}
        size="sm"
        onClick={() => editor.chain().focus().toggleBold().run()}
      >
        <Bold className="w-4 h-4" />
      </Button>
      
      <Button
        variant={editor.isActive('italic') ? 'default' : 'ghost'}
        size="sm"
        onClick={() => editor.chain().focus().toggleItalic().run()}
      >
        <Italic className="w-4 h-4" />
      </Button>
      
      <Button
        variant={editor.isActive('underline') ? 'default' : 'ghost'}
        size="sm"
        onClick={() => editor.chain().focus().toggleUnderline().run()}
      >
        <UnderlineIcon className="w-4 h-4" />
      </Button>

      <div className="w-px h-6 bg-slate-200 mx-1" />

      <Button
        variant={editor.isActive('bulletList') ? 'default' : 'ghost'}
        size="sm"
        onClick={() => editor.chain().focus().toggleBulletList().run()}
      >
        <List className="w-4 h-4" />
      </Button>
      
      <Button
        variant={editor.isActive('orderedList') ? 'default' : 'ghost'}
        size="sm"
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
      >
        <ListOrdered className="w-4 h-4" />
      </Button>

      <div className="w-px h-6 bg-slate-200 mx-1" />

      <Button
        variant={editor.isActive({ textAlign: 'left' }) ? 'default' : 'ghost'}
        size="sm"
        onClick={() => editor.chain().focus().setTextAlign('left').run()}
      >
        <AlignLeft className="w-4 h-4" />
      </Button>
      
      <Button
        variant={editor.isActive({ textAlign: 'center' }) ? 'default' : 'ghost'}
        size="sm"
        onClick={() => editor.chain().focus().setTextAlign('center').run()}
      >
        <AlignCenter className="w-4 h-4" />
      </Button>
      
      <Button
        variant={editor.isActive({ textAlign: 'right' }) ? 'default' : 'ghost'}
        size="sm"
        onClick={() => editor.chain().focus().setTextAlign('right').run()}
      >
        <AlignRight className="w-4 h-4" />
      </Button>

      <div className="w-px h-6 bg-slate-200 mx-1" />

      <Button
        variant="ghost"
        size="sm"
        onClick={addLink}
      >
        <LinkIcon className="w-4 h-4" />
      </Button>
      
      <Button
        variant="ghost"
        size="sm"
        onClick={addImage}
      >
        <ImageIcon className="w-4 h-4" />
      </Button>

      <div className="w-px h-6 bg-slate-200 mx-1" />

      <select
        className="px-2 py-1 border border-slate-200 rounded text-sm"
        onChange={(e) => {
          const level = parseInt(e.target.value);
          if (level === 0) {
            editor.chain().focus().setParagraph().run();
          } else {
            editor.chain().focus().toggleHeading({ level }).run();
          }
        }}
        value={
          editor.isActive('heading', { level: 1 }) ? 1 :
          editor.isActive('heading', { level: 2 }) ? 2 :
          editor.isActive('heading', { level: 3 }) ? 3 : 0
        }
      >
        <option value={0}>Paragraph</option>
        <option value={1}>Heading 1</option>
        <option value={2}>Heading 2</option>
        <option value={3}>Heading 3</option>
      </select>
    </div>
  );
}

export default function CreateArticle() {
  const [location, setLocation] = useLocation();
  const { user } = useAuth();
  const { toast } = useToast();
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  
  // Check if we're editing an existing article
  const urlParams = new URLSearchParams(window.location.search);
  const articleId = urlParams.get('id');
  const isEditing = !!articleId;
  
  const [formData, setFormData] = useState({
    title: '',
    authorId: '',
    publishDate: new Date().toISOString().split('T')[0],
    thumbnailUrl: '',
    module: 'general'
  });
  
  const [authors, setAuthors] = useState<AuthorData[]>([]);
  const [currentArticle, setCurrentArticle] = useState<any>(null);

  const editor = useEditor({
    extensions: [
      StarterKit,
      Image.configure({
        HTMLAttributes: {
          class: 'max-w-full h-auto rounded-lg',
        },
      }),
      TextAlign.configure({
        types: ['heading', 'paragraph'],
      }),
      Underline,
    ],
    content: '<p>Start writing your article...</p>',
    editorProps: {
      attributes: {
        class: 'prose prose-slate max-w-none min-h-[400px] p-4 focus:outline-none',
      },
    },
  });

  // Load authors and existing article data
  useEffect(() => {
    const loadData = async () => {
      if (user) {
        try {
          // Load authors
          const authorsData = await getAuthors();
          setAuthors(authorsData);
          
          // Load existing article if editing
          if (isEditing && articleId) {
            setLoading(true);
            const article = await getArticleById(articleId);
            if (article) {
              setCurrentArticle(article);
              setFormData({
                title: article.title || '',
                authorId: (article.contributorIds && article.contributorIds[0]) || article.authorId || '',
                publishDate: article.publishDate ? timestampToString(article.publishDate) : new Date().toISOString().split('T')[0],
                thumbnailUrl: article.thumbnailUrl || '',
                module: article.modules && article.modules.length > 0 ? article.modules[0] : 'general'
              });
              if (editor && article.content) {
                editor.commands.setContent(article.content);
              }
            }
          }
        } catch (error: any) {
          captureHandledException(error, "articles.load-editor-data");
          toast({
            title: "Failed to load data",
            description: error.message,
            variant: "destructive",
          });
        } finally {
          setLoading(false);
        }
      }
    };

    loadData();
  }, [isEditing, articleId, user, editor, toast]);

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const getPublishButtonText = () => {
    const publishDate = new Date(formData.publishDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    publishDate.setHours(0, 0, 0, 0);
    
    // If editing an existing article that's already Scheduled or Published, just say "Save"
    if (isEditing && currentArticle && (currentArticle.status === 'Scheduled' || currentArticle.status === 'Published')) {
      return 'Save';
    }
    
    if (isEditing) {
      if (publishDate > today) {
        return 'Schedule Article';
      } else {
        return 'Publish Article';
      }
    } else {
      if (publishDate > today) {
        return 'Schedule';
      } else {
        return 'Publish';
      }
    }
  };

  const getDraftButtonText = () => {
    // If editing an existing article that's already Scheduled or Published, say "Revert to Draft"
    if (isEditing && currentArticle && (currentArticle.status === 'Scheduled' || currentArticle.status === 'Published')) {
      return 'Revert to Draft';
    }
    
    return 'Save as Draft';
  };

  const handleSave = async (saveAsDraft = false) => {
    if (!editor || !user) return;

    if (!formData.title.trim()) {
      toast({
        title: "Title required",
        description: "Please enter a title for your article.",
        variant: "destructive",
      });
      return;
    }

    if (!formData.authorId) {
      toast({
        title: "Contributor required",
        description: "Please select a contributor.",
        variant: "destructive",
      });
      return;
    }
    
    const selectedAuthor = authors.find(a => a.id === formData.authorId);
    if (!selectedAuthor?.id) {
      toast({
        title: "Invalid contributor",
        description: "Please select a valid contributor.",
        variant: "destructive",
      });
      return;
    }

    setSaving(true);

    try {
      const htmlContent = editor.getHTML();
      const status = saveAsDraft ? 'Draft' : 'Published';
      
      if (isEditing && articleId) {
        // Update existing article
        const updates = {
          title: formData.title,
          contributorIds: [selectedAuthor.id],
          contributors: [{ id: selectedAuthor.id, name: selectedAuthor.name, profilePicUrl: selectedAuthor.profilePicUrl || '', qualifications: selectedAuthor.qualifications || '', story: selectedAuthor.story || '' }],
          content: htmlContent,
          publishDate: stringToTimestamp(formData.publishDate),
          contentType: 'article',
          modules: [formData.module],
          status: status as 'Draft' | 'Published' | 'Scheduled' | 'Blocked',
          ...(formData.thumbnailUrl && { thumbnailUrl: formData.thumbnailUrl })
        };

        await updateVideo(articleId, updates);

        toast({
          title: "Article updated",
          description: `"${formData.title}" has been ${status.toLowerCase()} successfully.`,
        });
      } else {
        // Create new article
        const articleData = {
          title: formData.title,
          contributorIds: [selectedAuthor.id],
          contributors: [{ id: selectedAuthor.id, name: selectedAuthor.name, profilePicUrl: selectedAuthor.profilePicUrl || '', qualifications: selectedAuthor.qualifications || '', story: selectedAuthor.story || '' }],
          channel: 'Article', // Default channel for articles
          description: formData.title, // Use title as description
          duration: '', // Not applicable for articles
          mediaType: 'article' as const,
          contentCategories: [], // Will be set when categories are assigned
          contentLibraries: [],
          modules: [formData.module],
          contentType: 'article',
          jobTypes: [],
          enabled: true,
          status: status as 'Draft' | 'Published' | 'Scheduled' | 'Blocked',
          url: '', // Will be generated or set later
          content: htmlContent,
          publishDate: stringToTimestamp(formData.publishDate),
          externalId: `article-${Date.now()}`, // Generate unique ID
          added: null, // Will be set by Firebase
          modified: null, // Will be set by Firebase
          ...(formData.thumbnailUrl && { thumbnailUrl: formData.thumbnailUrl })
        };

        await addVideo(user.uid, articleData);

        toast({
          title: `Article ${status.toLowerCase()}`,
          description: `"${formData.title}" has been ${status.toLowerCase()} successfully.`,
        });
      }

      setLocation('/articles');
    } catch (error: any) {
      captureHandledException(error, "articles.save");
      toast({
        title: "Failed to save article",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex-1 p-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8 flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setLocation('/articles')}
              className="flex items-center space-x-2"
            >
              <ArrowLeft size={16} />
              <span>Back to Articles</span>
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-slate-900">
                {isEditing ? 'Edit Article' : 'Create New Article'}
              </h1>
              <p className="text-slate-600">
                {isEditing ? 'Update your article content' : 'Write and publish your article content'}
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <Button
              variant="outline"
              onClick={() => handleSave(true)}
              disabled={saving}
              className="flex items-center space-x-2"
            >
              {saving ? (
                <>
                  <div className="w-4 h-4 border-2 border-slate-600 border-t-transparent rounded-full animate-spin" />
                  <span>Saving...</span>
                </>
              ) : (
                <>
                  <FileText size={16} />
                  <span>{getDraftButtonText()}</span>
                </>
              )}
            </Button>
            <Button
              onClick={() => handleSave(false)}
              disabled={saving}
              className="flex items-center space-x-2"
            >
              {saving ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  <span>Saving...</span>
                </>
              ) : (
                <>
                  <Save size={16} />
                  <span>{getPublishButtonText()}</span>
                </>
              )}
            </Button>
          </div>
        </div>

        <div className="space-y-6">
          {/* Article Details */}
          <Card>
            <CardHeader>
              <CardTitle>Article Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="title">Title *</Label>
                  <Input
                    id="title"
                    value={formData.title}
                    onChange={(e) => handleInputChange('title', e.target.value)}
                    placeholder="Enter article title"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="author">Contributor *</Label>
                  <Select 
                    value={formData.authorId} 
                    onValueChange={(value) => handleInputChange('authorId', value)}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="Select a contributor" />
                    </SelectTrigger>
                    <SelectContent>
                      {authors.map((author) => (
                        <SelectItem key={author.id} value={author.id || ''}>
                          <div className="flex items-center space-x-2">
                            {author.profilePicUrl ? (
                              <img
                                src={author.profilePicUrl}
                                alt={author.name}
                                className="w-6 h-6 rounded-full object-cover"
                              />
                            ) : (
                              <div className="w-6 h-6 bg-slate-200 rounded-full flex items-center justify-center">
                                <span className="text-xs text-slate-600">
                                  {author.name.charAt(0).toUpperCase()}
                                </span>
                              </div>
                            )}
                            <span>{author.name}</span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="space-y-4">
                <div>
                  <Label htmlFor="module">Module</Label>
                  <Select 
                    value={formData.module} 
                    onValueChange={(value) => handleInputChange('module', value)}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="general">General</SelectItem>
                      <SelectItem value="mind">Mind</SelectItem>
                      <SelectItem value="body">Body</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="publishDate">Publish Date</Label>
                    <Input
                      id="publishDate"
                      type="date"
                      value={formData.publishDate}
                      onChange={(e) => handleInputChange('publishDate', e.target.value)}
                      className="mt-1"
                    />
                  </div>
                  <ImageUpload
                    value={formData.thumbnailUrl}
                    onChange={(url) => handleInputChange('thumbnailUrl', url)}
                    label="Thumbnail Image"
                    uploadType="article"
                    articleId={articleId || undefined}
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Rich Text Editor */}
          <Card>
            <CardHeader>
              <CardTitle>Article Content</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <EditorToolbar editor={editor} />
              <EditorContent 
                editor={editor} 
                className="min-h-[500px] max-h-[600px] overflow-y-auto"
              />
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}